import { createContext, useContext, useState, useCallback } from 'react';
import { patients as initialPatients } from '@/data/mockData';
import { toast } from 'sonner';

const AppContext = createContext(null);

const HARDCODED_USER = 'adiadmin';
const HARDCODED_PASS = "Witapp2'25!?";

/* ═══ Mock Drafts ═══ */
const INITIAL_DRAFTS = [
  {
    id: 'draft-001',
    patientId: 2,
    formData: {
      cognitivo_C1: '1', cognitivo_C2A: '0', cognitivo_C2B: '1', cognitivo_C2C: '0',
      cognitivo_C2D: '1', cognitivo_D1: '0', cognitivo_D2A: '1', cognitivo_D2B: '0',
      umore_E1A: '1', umore_E1B: '0', umore_E1C: '1', umore_E1F: '0',
      umore_E1G: '1', umore_E1I: '0', umore_E2: '1', umore_E3: '0',
    },
    completedSections: 2,
    lastSectionIdx: 2,
    lastQuestionIdx: 0,
    lastModified: '16/04/2026, 14:32',
    createdAt: '15/04/2026, 09:15',
  },
  {
    id: 'draft-002',
    patientId: 3,
    formData: {
      cognitivo_C1: '2', cognitivo_C2A: '1', cognitivo_C2B: '1', cognitivo_C2C: '1',
      cognitivo_D1: '2', cognitivo_D2A: '1', cognitivo_D2B: '1',
    },
    completedSections: 0,
    lastSectionIdx: 0,
    lastQuestionIdx: 7,
    lastModified: '16/04/2026, 10:05',
    createdAt: '16/04/2026, 09:00',
  },
  {
    id: 'draft-003',
    patientId: 5,
    formData: {
      cognitivo_C1: '0', cognitivo_C2A: '0', cognitivo_C2B: '0', cognitivo_C2C: '0',
      cognitivo_D1: '1', cognitivo_D2A: '0', cognitivo_D2B: '0',
      umore_E1A: '0', umore_E1B: '1', umore_E1C: '0', umore_E1F: '1',
      umore_E1G: '0', umore_E1I: '0', umore_E2: '0', umore_E3: '1',
      funzione_fisica_G2A: '2', funzione_fisica_G2B: '1', funzione_fisica_G2E: '2',
      funzione_fisica_G2G: '1', funzione_fisica_G2I: '2', funzione_fisica_G2J: '1',
      condizioni_J1: '1', condizioni_J6A: '0', condizioni_J6B: '1', condizioni_J6C: '0',
    },
    completedSections: 3,
    lastSectionIdx: 3,
    lastQuestionIdx: 4,
    lastModified: '14/04/2026, 17:48',
    createdAt: '14/04/2026, 11:30',
  },
];

export function AppProvider({ children }) {
  const [patients, setPatients] = useState(initialPatients);
  const [notifications, setNotifications] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [drafts, setDrafts] = useState(INITIAL_DRAFTS);

  const login = useCallback((username, password) => {
    if (username === HARDCODED_USER && password === HARDCODED_PASS) {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setCurrentRole(null);
  }, []);

  const addPendingNotification = useCallback((targetRole, message, description) => {
    setPendingNotifications(prev => [...prev, {
      id: Date.now() + Math.random(),
      targetRole,
      message,
      description,
      timestamp: new Date().toLocaleString('it-IT'),
    }]);
  }, []);

  const showPendingNotifications = useCallback((role) => {
    setPendingNotifications(prev => {
      const toShow = prev.filter(n => n.targetRole === role);
      toShow.forEach(n => {
        toast.info(n.message, { description: n.description, duration: 5000 });
      });
      return prev.filter(n => n.targetRole !== role);
    });
  }, []);

  const acceptRequest = useCallback((patientId, medicoId, dataVisita, equipeArray) => {
    setPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        return { ...p, stato: 'presa_in_carico', medicoAssegnato: medicoId, dataVisitaUVMD: dataVisita, equipeAssegnata: equipeArray || [] };
      }
      return p;
    }));
    setNotifications(prev => [...prev, {
      id: Date.now(),
      tipo: 'success',
      messaggio: `Richiesta accettata e assegnata con successo`,
      timestamp: new Date().toLocaleString('it-IT'),
    }]);
    // Cross-role notification: Segreteria → Medico
    const patient = initialPatients.find(p => p.id === patientId);
    addPendingNotification(
      'medico',
      'Nuova valutazione assegnata',
      `${patient?.cognome || ''} ${patient?.nome || ''} - Visita UVMD prevista: ${dataVisita}`
    );
  }, [addPendingNotification]);

  const startEvaluation = useCallback((patientId) => {
    setPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        return { ...p, stato: 'in_valutazione' };
      }
      return p;
    }));
  }, []);

  const completeEvaluation = useCallback((patientId) => {
    setPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        return { ...p, stato: 'pai_completato', paiCompletato: true, dataPAI: new Date().toLocaleDateString('it-IT') };
      }
      return p;
    }));
    setNotifications(prev => [...prev, {
      id: Date.now(),
      tipo: 'info',
      messaggio: `PAI firmato digitalmente e inviato al Case Manager`,
      timestamp: new Date().toLocaleString('it-IT'),
    }]);
    // Cross-role notification: Medico → Case Manager
    const patient = initialPatients.find(p => p.id === patientId);
    addPendingNotification(
      'case-manager',
      'Nuovo PAI completato',
      `${patient?.cognome || ''} ${patient?.nome || ''} - PAI pronto per assegnazione provider`
    );
  }, [addPendingNotification]);

  const assignProvider = useCallback((patientId, providerIds) => {
    setPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        return { ...p, stato: 'assegnazione', providerAssegnati: providerIds };
      }
      return p;
    }));
    // Cross-role notification: Case Manager → Segreteria
    const patient = initialPatients.find(p => p.id === patientId);
    addPendingNotification(
      'segreteria',
      'Provider assegnati',
      `${patient?.cognome || ''} ${patient?.nome || ''} - Richiesta inviata a ${providerIds.length} provider`
    );
  }, [addPendingNotification]);

  const dismissNotification = useCallback((notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  }, []);

  const saveDraft = useCallback((patientId, formData, lastSectionIdx, lastQuestionIdx, completedSections) => {
    setDrafts(prev => {
      const existing = prev.findIndex(d => d.patientId === patientId);
      const now = new Date().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      const draft = {
        id: existing >= 0 ? prev[existing].id : `draft-${Date.now()}`,
        patientId,
        formData: { ...formData },
        completedSections,
        lastSectionIdx,
        lastQuestionIdx,
        lastModified: now,
        createdAt: existing >= 0 ? prev[existing].createdAt : now,
      };
      if (existing >= 0) {
        return prev.map((d, i) => i === existing ? draft : d);
      }
      return [draft, ...prev];
    });
  }, []);

  const deleteDraft = useCallback((draftId) => {
    setDrafts(prev => prev.filter(d => d.id !== draftId));
  }, []);

  const getDraft = useCallback((patientId) => {
    return drafts.find(d => d.patientId === patientId);
  }, [drafts]);

  return (
    <AppContext.Provider value={{
      patients,
      notifications,
      currentRole,
      setCurrentRole,
      isAuthenticated,
      login,
      logout,
      acceptRequest,
      startEvaluation,
      completeEvaluation,
      assignProvider,
      dismissNotification,
      pendingNotifications,
      showPendingNotifications,
      drafts,
      saveDraft,
      deleteDraft,
      getDraft,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
